/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNotifications } from '@newfront-insurance/next-notifications';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, useSuspenseQuery, keepPreviousData } from '@tanstack/react-query';
import type { TRPCClient } from '@trpc/client';
import type { AnyRouter } from '@trpc/server';
import { useCallback } from 'react';
import { getArgs, getCacheKey } from './internals/utils';
import type { TRPCClientHooks } from './types';
import { getPreferredErrorMessage } from '../errors/utils';

/**
 * Thin wrapper over react-query to make our react-query usage type-safe within TRPC.
 *
 * react-query can always be used without these hooks.
 *
 * @param client TRPCClient
 * @returns TRPCClientHooks
 */
export function useClientQueryHooks<Router extends AnyRouter>(client: TRPCClient<Router>): TRPCClientHooks<Router> {
  const queryClient = useQueryClient();
  const clientQueryHooks: Omit<TRPCClientHooks<Router>, 'useErrorHandledMutation'> = {
    useQuery: (pathAndInput, opts) => {
      const {
        // Skip these options as they're no longer supported as valid options
        keepPreviousData: isKeepPreviousData,
        // Keep the rest of the options
        ...rest
      } = opts ?? {};
      const useQueryOptions = {
        ...rest,
        ...(isKeepPreviousData ? {
          placeholderData: keepPreviousData
        } : {})
      };
      const queryOptions = {
        queryKey: getCacheKey(pathAndInput) as any,
        queryFn: () => (client as any).query(...getArgs(pathAndInput, useQueryOptions)) as any,
        ...useQueryOptions
      };
      return useQuery(queryOptions);
    },
    useSuspenseQuery: (pathAndInput_0, opts_0) => {
      return useSuspenseQuery({
        queryKey: getCacheKey(pathAndInput_0) as any,
        queryFn: () => (client as any).query(...getArgs(pathAndInput_0, opts_0)) as any,
        ...opts_0
      });
    },
    useInfiniteQuery: (pathAndInput_1, opts_1) => {
      const [path, input] = pathAndInput_1;
      return useInfiniteQuery({
        queryKey: getCacheKey(pathAndInput_1) as any,
        queryFn: ({
          pageParam
        }) => {
          const actualInput = {
            ...(input as any ?? {}),
            cursor: pageParam
          };
          return (client as any).query(...getArgs([path, actualInput], opts_1));
        },
        ...opts_1
      });
    },
    useMutation: (path_0, opts_2) => {
      const mutationResponse = useMutation({
        mutationFn: input_0 => (client.mutation as any)(path_0, input_0),
        ...opts_2
      });
      return {
        ...mutationResponse,
        isLoading: mutationResponse.isPending
      };
    },
    fetchQuery: useCallback((pathAndInput_2, opts_3) => {
      const cacheKey = getCacheKey(pathAndInput_2);
      return queryClient.fetchQuery({
        queryKey: cacheKey,
        queryFn: () => (client as any).query(...getArgs(pathAndInput_2, opts_3)),
        ...opts_3
      });
    }, [client, queryClient]),
    prefetchQuery: useCallback((pathAndInput_3, opts_4) => {
      const cacheKey_0 = getCacheKey(pathAndInput_3);
      return queryClient.prefetchQuery({
        queryKey: cacheKey_0,
        queryFn: () => (client as any).query(...getArgs(pathAndInput_3, opts_4)),
        ...opts_4
      });
    }, [client, queryClient]),
    invalidateQuery: useCallback((pathAndInput_4, filters) => {
      return queryClient.invalidateQueries({
        queryKey: getCacheKey(pathAndInput_4),
        ...filters
      });
    }, [queryClient]),
    cancelQuery: useCallback(pathAndInput_5 => {
      return queryClient.cancelQueries({
        queryKey: getCacheKey(pathAndInput_5) as any
      });
    }, [queryClient]),
    setQueryData: useCallback((pathAndInput_6, output) => {
      const cacheKey_1 = getCacheKey(pathAndInput_6);
      queryClient.setQueryData(cacheKey_1, output);
    }, [queryClient]),
    setQueriesData: useCallback((pathAndInput_7, output_0) => {
      const cacheKeyOrFilter = Array.isArray(pathAndInput_7) ? getCacheKey(pathAndInput_7) : pathAndInput_7;
      queryClient.setQueriesData(cacheKeyOrFilter as any, output_0);
    }, [queryClient]),
    setInfiniteQueryData: useCallback((pathAndInput_8, output_1) => {
      const cacheKey_2 = getCacheKey(pathAndInput_8);
      queryClient.setQueryData(cacheKey_2, output_1);
    }, [queryClient]),
    getQueryData: useCallback(pathAndInput_9 => {
      const cacheKey_3 = getCacheKey(pathAndInput_9);
      return queryClient.getQueryData(cacheKey_3);
    }, [queryClient])
  };
  return {
    ...clientQueryHooks,
    // This is defined here to reuse our useMutation abstraction
    // and assign onError props using our own type that difers
    // from the react-query useMutation error.

    // NOTE: Ideally the onError here will catch all errors and handle them internally, meaning
    // we dont need the define an onError handler on the consumer - we cand just pass a defaultErrorMessage to be used onError.
    // However we still allow the consumer to handle the onError themselves for situations like optimistic updates.
    useErrorHandledMutation: (path_1, opts_5, defaultErrorMessage?: string) => {
      const notifications = useNotifications();
      return clientQueryHooks.useMutation(path_1, {
        ...opts_5,
        /**
         * ⚠️ @warning Consider using defaultErrorMessage parameter instead of onError for consistent error handling.
         * Example: useErrorHandledMutation('path', {}, 'Custom error message')
         * Only use onError if you need access perform actions like optimistic updates.
         */
        onError: (err, _input, _context) => {
          if (opts_5?.onError) {
            opts_5.onError(err, _input, _context);
            return;
          }

          // Check multiple paths for error information
          const statusCode = err.data?.httpStatus || (err as any).originalError?.responseBody?.error?.json?.fetcherError?.fetcherResponse?.statusCode;
          if (statusCode && statusCode >= 400 && statusCode < 500) {
            notifications.add({
              type: 'error',
              title: getPreferredErrorMessage(err)
            });
            return;
          }

          // Handle all other errors (including 5XX) internally
          notifications.add({
            type: 'error',
            title: defaultErrorMessage ?? getPreferredErrorMessage(err)
          });
        }
      });
    }
  };
}